import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid } from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconResetSmall } from "@mzc-cloudplex/icons";
import { StyledGrid } from "@components_v2/grid/grid.styled";
import FolderLocation from "@components_v2/folder-location/folder-location";
import RecentFolderLocation from "@components_v2/recent-folder-location/recent-folder-location";
import SelectFolderPathModal from "@components/modals/SelectFolderPath";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import { updateData } from "@modules/createAsset";

const CreateAssetLocationSection = ({ initialFolder }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getVirtualFolderFullPathList } = useVirtualFolderPath({ domain: "ASSET" });

    const [folderLocation, setFolderLocation] = useState([]);
    const [folderPathModalOpen, setFolderPathModalOpen] = useState(false);

    const onHandleFolderPath = ({ folderId, folderPath }) => {
        const fLocation = folderPath?.map((item) => ({ name: item.name }));
        dispatch(updateData({ virtualFolder: { id: folderId } }));
        setFolderLocation(fLocation);
        setFolderPathModalOpen(false);
    };

    const onChangeRecentLocation = ({ locationId, locations }) => {
        dispatch(updateData({ virtualFolder: { id: locationId } }));
        setFolderLocation(locations);
    };

    const handleInitialFolder = async (folder) => {
        if (!folder) return;
        if (!folder.folderPath) {
            try {
                folder.folderPath = await getVirtualFolderFullPathList({
                    folderId: folder.folderId,
                    includeFolder: true,
                });
            } catch (error) {
                console.error("Failed to load folder path:", error);
                return;
            }
        }
        onHandleFolderPath({ folderId: folder.folderId, folderPath: folder.folderPath });
    };

    useEffect(() => {
        handleInitialFolder(initialFolder);
        return () => {
            setFolderLocation([]);
        };
    }, []);

    return (
        <>
            {folderPathModalOpen && (
                <SelectFolderPathModal
                    open
                    domain={"Asset"}
                    submitText={`Select`}
                    width={"600px"}
                    title={"Select location"}
                    subFoldersOption={false}
                    onSubmit={onHandleFolderPath}
                    onClose={() => setFolderPathModalOpen(false)}
                />
            )}
            <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                <Typography
                    variant={`body1`}
                    component={"p"}
                    color={theme.palette.secondary[800]}
                    required={true}
                >
                    {t(`common::label::Location`)}
                </Typography>
                <Divider orientation="vertical" flexItem />
                <StyledGrid item theme={theme}>
                    <Grid container flexDirection={"row"} gap={2} alignItems={"center"}>
                        <Grid item sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                            <FolderLocation
                                folderLocation={folderLocation}
                                onClickChange={() => setFolderPathModalOpen(true)}
                            />
                        </Grid>
                        <Grid item>
                            <RecentFolderLocation
                                domain={"ASSET"}
                                onChangeRecentLocation={onChangeRecentLocation}
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                disabled={folderLocation?.length === 0}
                                size={"small"}
                                variant={"text"}
                                startIcon={<IconResetSmall size={16} />}
                                color={"greySecondary"}
                                onClick={() =>
                                    initialFolder
                                        ? handleInitialFolder(initialFolder)
                                        : setFolderLocation([])
                                }
                            >
                                <Typography variant={"body3"}>
                                    {t(`common::label::Reset`)}
                                </Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </StyledGrid>
            </Grid>
        </>
    );
};

export default CreateAssetLocationSection;
